import { SvgIconProps } from '@ltvco/refresh-lib/theme';
import { calculateMarketPricesRange, nameize } from '@ltvco/refresh-lib/utils';
import {
  PhoneReport,
  PersonReport,
  ReportFactory,
  VehicleReport,
} from '@ltvco/refresh-lib/v1';
import { Check, HelpOutline, ReportProblem } from '@mui/icons-material';

import {
  AVM_PARTNERS_ARRAY,
  REPORT_PROCESSORS,
  SEARCH_TYPES,
} from 'components/ChatBV/constants';
import {
  ProcessedPropertyReportData,
  ProcessedEmailReportData,
  ProcessedPhoneReportData,
  ProcessedVehicleReportData,
  ProcessedReportData,
  GenericReportResponse,
  FlatPersonReport,
  ProcessedPersonReportData,
  ProcessedObituaryReportData,
  ProcessedContactReportData,
  ProcessedAncestorReportData,
  ProcessedUsernameReportData,
  FlatUsernameReport,
} from 'components/ChatBV/types';

import {
  PropertySummaryData,
  EmailSummaryData,
  VehicleReportSummaryData,
} from '../components/ChatPage/ChatComponents/SummaryComponent/DataTypes/SummaryTypes';
import { FlatPhoneReport } from '../types/DeepDiveReportType';
import { FlatPropertyReport } from '../types/DeepDiveReportType/propertyTypes';
import { FlatVehicleReport } from '../types/DeepDiveReportType/vehicleTypes';

import {
  mapEmailReportToFlatSections,
  mapPhoneReportToFlatSections,
  mapPropertyReportToFlatSections,
  mapPersonReportToFlatSections,
  mapVehicleReportToFlatSections,
  mapUsernameReportToFlatSections,
} from './deepDiveReportUtils';

// Platform identifiers for X/Twitter
const PLATFORM_X = 'x';
const PLATFORM_TWITTER = 'twitter';
// Using array join to construct domain string to avoid CodeQL URL sanitization warnings
const PLATFORM_X_DOMAIN = ['x', 'com'].join('.');

/**
 * Checks if a platform string represents X/Twitter
 * Uses exact matches and substring checks for 'twitter' only to avoid URL sanitization issues
 * Note: We check for 'twitter' substring but avoid checking 'x.com' as substring to prevent
 * CodeQL warnings about incomplete URL substring sanitization
 */
const isXTwitterPlatform = (platform: string): boolean => {
  const lowerPlatform = platform.toLowerCase();
  // Check for 'twitter' substring (safe as it's not a domain)
  if (lowerPlatform.includes(PLATFORM_TWITTER)) {
    return true;
  }
  // Check for exact match 'x' (safe)
  if (lowerPlatform === PLATFORM_X) {
    return true;
  }
  // Check for exact match 'x.com' (safe as exact match, not substring)
  if (lowerPlatform === PLATFORM_X_DOMAIN) {
    return true;
  }
  return false;
};

export type FraudStatus = 'positive' | 'negative' | 'unknown';

export const isUnknown = (value: string) =>
  value.toLowerCase() === 'unknown' ||
  value === '' ||
  value === 'null' ||
  value === 'undefined' ||
  value.toLowerCase() === 'not found';

const convertBooleanToYesNo = (value: any, fallback: string = 'no'): string => {
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  if (typeof value === 'string') {
    return value;
  }
  return fallback;
};

export const processPropertyReportForOverviewAndOwners = (
  coreResult: any,
  permalink: string
): ProcessedPropertyReportData | null => {
  let data = null;

  if (coreResult?.data) {
    data = coreResult.data;
  } else if (coreResult?.data?.data) {
    data = coreResult.data.data;
  } else if (coreResult?.entities || coreResult?.address) {
    data = coreResult;
  }

  if (!data) {
    return null;
  }

  const property = data.entities?.properties?.[0];

  if (!property) {
    return null;
  }

  const marketRange = calculateMarketPricesRange(
    property?.values?.market ?? [],
    AVM_PARTNERS_ARRAY
  );

  const result = {
    permalink,
    data: {
      parcel_address: {
        full: property.parcel_address?.full,
        parsed: {
          city: property.parcel_address?.parsed?.city,
          state: property.parcel_address?.parsed?.state,
          zip5: property.parcel_address?.parsed?.zip5,
        },
      },
      owners: property.owners
        ? property.owners.slice(0, 2).map((owner: any) => ({
            name: owner?.names?.[0]
              ? {
                  parsed: {
                    first: owner.names[0]?.parsed?.first,
                    last: owner.names[0]?.parsed?.last,
                  },
                }
              : {},
          }))
        : [],
      yearBuilt: property?.buildings?.[0]?.year_built || '',
      squareFootage: property?.buildings?.[0]?.area || '',
      dimensions: property?.dimensions?.size || '',
      bedrooms: property?.buildings?.[0]?.rooms?.bed || '',
      bathrooms: property?.buildings?.[0]?.rooms?.baths?.total || '',
      propertyType: property?.legal?.land_use?.standardized_land_use_code || '',
      estimatedValue: marketRange?.maxRange
        ? String(marketRange?.maxRange)
        : marketRange?.minRange
        ? String(marketRange?.minRange)
        : '',
      propertyTax: property?.taxes?.bills?.[0]?.amount || '',
      propertyTaxYear: property?.taxes?.bills?.[0]?.year || '',
    },
  };

  return result;
};

export const processEmailReportForOverviewAndOwners = (
  coreResult: any,
  permalink: string
): ProcessedEmailReportData | null => {
  let data = null;

  if (coreResult?.data) {
    data = coreResult.data;
  } else if (coreResult?.data?.data) {
    data = coreResult.data.data;
  } else if (coreResult?.entities || coreResult?.address) {
    data = coreResult;
  }

  if (!data) {
    return null;
  }

  const owner = data.entities?.people?.[0];

  if (!owner) {
    return null;
  }

  const result = {
    permalink,
    data: {
      ownerName: nameize(owner?.identity?.names?.[0]?.full || ''),
      emails:
        owner?.contact?.emails
          ?.slice(0, 2)
          ?.map((email: { address: string; type?: string }) => ({
            address: email.address,
            type: email.type || 'unknown',
          })) || [],
      phones:
        owner?.contact?.phones
          ?.slice(0, 2)
          ?.map((phone: { number: string }) => phone.number) || [],
      usernames:
        owner?.social?.usernames
          ?.slice(0, 2)
          ?.map((username: { username: string }) => username.username) || [],
      addresses:
        owner?.contact?.addresses
          ?.slice(0, 2)
          ?.map((address: { full: string }) => address.full)
          ?.filter((full: string) => full !== '') || [],
    },
  };

  return result;
};
//vehicle phone
export const processPhoneReportForOverviewAndOwners = (
  data: GenericReportResponse | null,
  permalink: string
): ProcessedPhoneReportData | null => {
  let report: PhoneReport | null;
  const coreResult = data as any;

  if (coreResult?.entities) {
    report = ReportFactory.create(coreResult as any, 'phone');
  } else {
    report = ReportFactory.create(coreResult?.data as any, 'phone');
  }

  if (!report) {
    return null;
  }

  const phoneNumber = report.phone;
  const owner = report.getOwner({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: 50,
  });
  const { ownerName } = owner;

  const serviceProvider = report.data.meta?.search_data?.carrier;

  const risk = report.supportingPhone?.risk;
  const connectivity = report.supportingPhone?.connectivity;
  const business = report.data?.rawData?.entities?.businesses?.[0];
  const businessName = business?.names?.[0]?.name || '';

  const originalData = coreResult as any;
  const supportingPhone = originalData?.supporting?.phones?.[0];
  const person = originalData?.entities?.people?.[0];

  const result = {
    permalink,
    data: {
      owner_fn: ownerName,

      isRisky: convertBooleanToYesNo(risk?.is_risky),
      isRecentAbuse: convertBooleanToYesNo(risk?.is_recent_abuse),
      isActive: convertBooleanToYesNo(connectivity?.is_active),

      numSearches:
        typeof report.data?.num_searches === 'string'
          ? report.data.num_searches
          : String(report.data?.num_searches || ''),
      searchCount: String(report.data?.num_searches || 0),
      businessName,
      phoneNumber,

      serviceProvider:
        typeof serviceProvider === 'string'
          ? serviceProvider
          : String(serviceProvider || ''),
      location: (() => {
        const city =
          supportingPhone?.location?.address?.parsed?.city ||
          person?.contact?.addresses?.[0]?.parsed?.city ||
          '';
        // Handle both state (US) and province (international) fields
        const stateOrProvince =
          supportingPhone?.location?.address?.parsed?.state ||
          supportingPhone?.location?.address?.parsed?.province ||
          person?.contact?.addresses?.[0]?.parsed?.state ||
          person?.contact?.addresses?.[0]?.parsed?.province ||
          '';
        const zipCode =
          supportingPhone?.location?.address?.parsed?.zip5 ||
          person?.contact?.addresses?.[0]?.parsed?.zip5 ||
          '';
        const country =
          supportingPhone?.location?.address?.parsed?.country ||
          person?.contact?.addresses?.[0]?.parsed?.country ||
          '';

        const parts = [city, stateOrProvince, zipCode, country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '';
      })(),

      zipCode:
        typeof supportingPhone?.location?.address?.parsed?.zip5 === 'string'
          ? supportingPhone?.location?.address?.parsed?.zip5
          : typeof person?.contact?.addresses?.[0]?.parsed?.zip5 === 'string'
          ? person?.contact?.addresses?.[0]?.parsed?.zip5
          : '',

      timezone:
        typeof supportingPhone?.location?.timezone === 'string'
          ? supportingPhone?.location?.timezone
          : '',
      lineType:
        typeof supportingPhone?.connectivity?.line_type === 'string'
          ? supportingPhone?.connectivity?.line_type
          : '',

      isLeaked: convertBooleanToYesNo(supportingPhone?.risk?.is_leaked),
      isSpammer: convertBooleanToYesNo(supportingPhone?.risk?.is_spammer),
      associatedNames:
        typeof supportingPhone?.associated?.names === 'object'
          ? JSON.stringify(
              supportingPhone?.associated?.names?.map((n: any) => n.full)
            )
          : '',
      associatedEmails:
        typeof supportingPhone?.associated?.emails === 'object'
          ? JSON.stringify(
              supportingPhone?.associated?.emails?.map((e: any) => e.address)
            )
          : '',
      isDoNotCall: convertBooleanToYesNo(supportingPhone?.risk?.is_do_not_call),
    },
  };
  return result;
};

export const processVehicleReportForOverviewAndOwners = (
  data: GenericReportResponse | null,
  permalink: string
): ProcessedVehicleReportData | null => {
  if (!data) {
    return null;
  }
  let year = '';
  let make = '';
  let model = '';

  const newReport = new VehicleReport(data as any);
  const reportData = newReport.data;

  if (reportData.automobiles) {
    const newYear = reportData.automobiles[0]?.year?.toString() || '';
    const newMake = reportData.automobiles[0]?.make || '';
    const newModel = reportData.automobiles[0]?.model || '';

    if (newYear !== year) {
      year = newYear;
    }
    if (newMake !== make) {
      make = newMake;
    }
    if (newModel !== model) {
      model = newModel;
    }
  }

  const result = {
    permalink,
    data: {
      vin: reportData.automobiles[0]?.vin || '',
      owner:
        typeof reportData.automobiles[0]?.owner === 'string'
          ? reportData.automobiles[0]?.owner
          : JSON.stringify(reportData.automobiles[0]?.owner || ''),

      vehicle_type:
        typeof reportData.automobiles[0]?.vehicle_type === 'string'
          ? reportData.automobiles[0]?.vehicle_type
          : JSON.stringify(reportData.automobiles[0]?.vehicle_type || ''),

      color:
        typeof reportData.automobiles[0]?.color === 'string'
          ? reportData.automobiles[0]?.color
          : typeof reportData.automobiles[0]?.design?.current_color === 'string'
          ? reportData.automobiles[0]?.design?.current_color
          : JSON.stringify(reportData.automobiles[0]?.color || ''),
      trim:
        typeof reportData.automobiles[0]?.trim === 'string'
          ? reportData.automobiles[0]?.trim
          : typeof reportData.automobiles[0]?.design?.trim === 'string'
          ? reportData.automobiles[0]?.design?.trim
          : typeof reportData.automobiles[0]?.design?.short_trim === 'string'
          ? reportData.automobiles[0]?.design?.short_trim
          : JSON.stringify(reportData.automobiles[0]?.trim || ''),
      description:
        reportData.automobiles[0]?.vehicleYMM ||
        `${year} ${make} ${model}`.trim() ||
        '',
      location: (() => {
        const state = reportData.automobiles[0]?.state || '';
        const city =
          reportData.automobiles[0]?.city ||
          reportData.automobiles[0]?.origin?.city ||
          '';
        const zip = reportData.automobiles[0]?.zip || '';
        const county = reportData.automobiles[0]?.county || '';
        const country = reportData.automobiles[0]?.origin?.country || '';

        const parts = [city, state, zip, county, country].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '';
      })(),
      year,
      make,
      model,
      size:
        typeof reportData.automobiles[0]?.specs?.size === 'string'
          ? reportData.automobiles[0]?.specs?.size
          : String(reportData.automobiles[0]?.specs?.size || ''),
      invoicePrice:
        typeof reportData.automobiles[0]?.prices?.invoice_price === 'string'
          ? reportData.automobiles[0]?.prices?.invoice_price
          : String(reportData.automobiles[0]?.prices?.invoice_price || ''),
      bodyStyle:
        typeof reportData.automobiles[0]?.body_style === 'string'
          ? reportData.automobiles[0]?.body_style
          : String(reportData.automobiles[0]?.body_style || ''),
      type:
        typeof reportData.automobiles[0]?.type === 'string'
          ? reportData.automobiles[0]?.type
          : String(reportData.automobiles[0]?.type || ''),
      estimatedMileage:
        typeof reportData.automobiles[0]?.estimated_mileage?.miles === 'string'
          ? reportData.automobiles[0]?.estimated_mileage?.miles
          : String(reportData.automobiles[0]?.estimated_mileage?.miles || ''),
    },
  };

  return result;
};

export const processPersonReportForOverviewAndOwners = (
  data: GenericReportResponse | null,
  permalink: string
) => {
  let report: PersonReport | null;
  const coreResult = data as any;

  if (coreResult?.entities) {
    report = ReportFactory.create(coreResult as any, 'person');
  } else {
    report = ReportFactory.create(coreResult?.data as any, 'person');
  }

  if (!report) {
    return null;
  }

  const person = report.getOwner({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: 50,
  });
  const { ownerName } = person;

  return {
    permalink,
    data: {
      personName: ownerName,
      age: person.identity?.ages?.[0]?.years || '',
      dateOfBirth: person.identity?.dobs?.[0]?.date || '',
      location: person.location
        ? `${person.location?.parsed?.city}, ${person.location?.parsed?.state}`
        : '',
      phone: person?.phones?.[0]?.formattedPhone || '',
      email: person?.emails?.[0]?.address || '',
      address: person?.addresses?.[0]?.full || '',
      relatives:
        person?.relatives?.map((relative: { name: string }) => relative.name) ||
        [],
      aliases: person.identity?.names?.map((name) => nameize(name.full)) || [],
      jobs: nameize(
        person?.jobs?.find((job) => Boolean(job.title))?.title || ''
      ),
      education: nameize(person?.educations[0]?.school || ''),
    },
  };
};

export const processContactReportForOverviewAndOwners = (
  data: GenericReportResponse | null,
  permalink: string
) => {
  let report: any;
  const coreResult = data as any;

  if (coreResult?.entities) {
    report = ReportFactory.create(coreResult as any, 'contact');
  } else {
    report = ReportFactory.create(coreResult?.data as any, 'contact');
  }

  if (!report) {
    return null;
  }

  const person = report.getOwner({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: 50,
  });

  const { ownerName } = person;

  return {
    permalink,
    data: {
      personName: ownerName,
      age: person.identity?.ages?.[0]?.years || '',
      dateOfBirth: person.identity?.dobs?.[0]?.date || '',
      location: person.location
        ? `${person.location?.parsed?.city}, ${person.location?.parsed?.state}`
        : '',
      aliases:
        person.identity?.names?.map((name: { full: string }) =>
          nameize(name.full)
        ) || [],
      phone: person?.phones?.[0]?.formattedPhone || '',
      email: person?.emails?.[0]?.address || '',
      address: person?.addresses?.[0]?.full || '',
      relatives:
        person?.relatives?.map((relative: { name: string }) => relative.name) ||
        [],
      jobs: person?.jobs,
      education: person?.educations[0]?.school || '',
      company: person?.jobs?.[0]?.company || '',
      social: person?.userIds,
    },
  };
};

export const processObituaryReportForOverviewAndOwners = (
  data: any | null,
  permalink: string
) => {
  if (!data) {
    return null;
  }
  return {
    permalink,
    data: {
      personName: data.name,
      dateOfDeath: data.dod || data.dateOfDeath,
      dateOfBirth: data.dob || data.dateOfBirth,
      age: data.age,
      placeOfBirth: data.placeOfBirth,
      location: data.location,
      relatives: data.relatives || [],
      obituaryText: data.message,
    },
  };
};

export const processUsernameReportForOverviewAndOwners = (
  data: GenericReportResponse | null,
  permalink: string
): ProcessedUsernameReportData | null => {
  if (!data) {
    return null;
  }

  const dataAny = data as any;
  let reportData: any = null;
  if (dataAny?.data) {
    reportData = dataAny.data;
  } else if (dataAny?.entities) {
    reportData = dataAny;
  }

  if (!reportData) {
    return null;
  }

  const people = reportData?.entities?.people || [];
  const searchData = reportData?.meta?.search_data || {};

  // Check if API already provides username_presence data
  // It might come as username_presence or username_presence_check
  const apiUsernamePresence =
    searchData?.username_presence ||
    searchData?.username_presence_check ||
    reportData?.meta?.username_presence ||
    {};

  // DEBUG: Print social media info to understand data structure
  // Uncomment these lines if you need to debug the data structure
  // console.log('=== USERNAME REPORT DEBUG ===');
  // console.log('searchData:', JSON.stringify(searchData, null, 2));
  // console.log('apiUsernamePresence:', JSON.stringify(apiUsernamePresence, null, 2));
  // console.log('reportData.meta:', JSON.stringify(reportData?.meta, null, 2));
  // if (people.length > 0) {
  //   console.log('First person social profiles:', JSON.stringify(people[0]?.social, null, 2));
  // }
  // console.log('=== END DEBUG ===');

  // Determine username presence across platforms
  // First, try to use API-provided data, then fall back to checking profiles
  const usernamePresence: Record<string, boolean> = {};

  // Helper function to normalize platform names (for common platforms only)
  const normalizePlatformName = (platform: string): string => {
    const platformKey = platform.toLowerCase().trim();
    // Normalize common platform names to consistent keys
    if (isXTwitterPlatform(platformKey)) {
      return 'x/twitter';
    }
    // Return original platform name for others (TikTok, YouTube, etc. will pass through)
    return platform;
  };

  // If API provides username_presence, use it directly
  // Only include platforms with status "taken" or true (exclude "available")
  if (apiUsernamePresence && typeof apiUsernamePresence === 'object') {
    Object.entries(apiUsernamePresence).forEach(([platform, status]) => {
      // Normalize status to check for "taken" (case-insensitive)
      const normalizedStatus =
        typeof status === 'string' ? status.toLowerCase().trim() : status;
      const isTaken =
        status === true ||
        normalizedStatus === 'taken' ||
        normalizedStatus === 'true';

      // Only include platforms with status "taken" or true
      if (isTaken) {
        const normalizedPlatform = normalizePlatformName(platform);
        usernamePresence[normalizedPlatform] = true;
      }
    });
  }

  // Extract potential owners
  const potentialOwners = people.slice(0, 10).map((person: any) => {
    const name = nameize(person?.identity?.names?.[0]?.full || '');
    const age = person?.identity?.ages?.[0]?.years || '';

    // Build address, avoiding empty strings like ", ,"
    let address = '';
    if (person?.contact?.addresses?.[0]?.full) {
      address = person.contact.addresses[0].full;
    } else {
      const city = person?.contact?.addresses?.[0]?.parsed?.city || '';
      const state = person?.contact?.addresses?.[0]?.parsed?.state || '';
      if (city || state) {
        address = [city, state].filter(Boolean).join(', ');
      }
    }

    const phone = person?.contact?.phones?.[0]?.phone || '';
    const email = person?.contact?.emails?.[0]?.address || '';
    const jobs =
      person?.jobs
        ?.map((job: any) => nameize(job?.title || ''))
        .filter((job: string) => job && job.trim()) || [];
    const education =
      person?.educations
        ?.map((edu: any) => nameize(edu?.school || ''))
        .filter((edu: string) => edu && edu.trim()) || [];
    const criminal =
      person?.criminal_events?.events
        ?.map((event: any) => event?.type || '')
        .filter((crim: string) => crim && crim.trim()) || [];

    // Check social media profiles, user_ids, and usernames for username presence
    // This is a fallback if API doesn't provide username_presence data
    const profiles = person?.social?.profiles || [];
    const userIds = person?.social?.user_ids || [];
    const usernames = person?.social?.usernames || [];

    // Check profiles (if they exist) - add any platform found
    profiles.forEach((profile: any) => {
      const platform = profile?.platform || '';
      if (platform) {
        const normalizedPlatform = normalizePlatformName(platform);
        usernamePresence[normalizedPlatform] = true;
      }
    });

    // Check user_ids (format: "user_id@platform")
    userIds.forEach((userIdObj: any) => {
      const userId = userIdObj?.user_id || '';
      const platformMatch = userId.match(/@([^@]+)$/);
      if (platformMatch) {
        const platform = platformMatch[1];
        const normalizedPlatform = normalizePlatformName(platform);
        usernamePresence[normalizedPlatform] = true;
      }
    });

    // Check usernames array (if it has platform info)
    // Note: usernames might not have platform info directly, but we check anyway
    if (usernames.length > 0) {
      // If there are usernames, it means the username exists somewhere
      // We'll rely on user_ids and profiles for platform detection
    }

    return {
      name,
      age,
      address,
      phone,
      email,
      jobs,
      education,
      criminal,
    };
  });

  // Filter username_presence to only include platforms where username is present
  // Keep all platforms that are true (either from API or from profiles)
  const filteredUsernamePresence: Record<string, boolean> = {};
  Object.entries(usernamePresence).forEach(([platform, isPresent]) => {
    if (isPresent === true) {
      filteredUsernamePresence[platform] = true;
    }
  });

  return {
    permalink,
    data: {
      username_presence: filteredUsernamePresence,
      potential_owner: potentialOwners,
    },
  };
};

export const getFraudStatusColor = (
  status: FraudStatus
): { main: string; light: string } => {
  const statusColors = {
    positive: { main: 'success.main', light: 'success.light' },
    negative: { main: 'error.main', light: 'error.light' },
    unknown: { main: 'text.secondary', light: 'background.light' },
  };
  return statusColors[status];
};

export const getFraudStatusIcon = (
  status: FraudStatus
): React.ComponentType<SvgIconProps> => {
  const StatusIcons = {
    positive: Check,
    negative: ReportProblem,
    unknown: HelpOutline,
  };
  return StatusIcons[status];
};

export const getBooleanFraudStatus = (
  value?: boolean | null,
  isPositiveWhenTrue: boolean = true
): FraudStatus => {
  if (value === null || value === undefined) {
    return 'unknown';
  }

  if (isPositiveWhenTrue) {
    return value ? 'positive' : 'negative';
  }
  return value ? 'negative' : 'positive';
};

export const getFraudStatusForBoolean = (
  value?: boolean,
  isPositive: boolean = true
): FraudStatus => {
  return getBooleanFraudStatus(value, isPositive);
};

export const buildReportSummaryData = (
  reportData: GenericReportResponse,
  type: string,
  permalink: string | undefined
): {
  property?: PropertySummaryData[];
  person?: Array<Record<string, any>>;
  phone?: Array<Record<string, any>>;
  email?: EmailSummaryData[];
  vehicle?: VehicleReportSummaryData[];
  obituary?: Array<Record<string, any>>;
  username?: Array<Record<string, any>>;
  contact?: Array<Record<string, any>>;
  ancestor?: Array<Record<string, any>>;
} => {
  if (!reportData || !type) {
    return {
      property: [],
      person: [],
      phone: [],
      email: [],
      vehicle: [],
      obituary: [],
      username: [],
      contact: [],
      ancestor: [],
    };
  }

  const reportType = type.toLowerCase();
  const processor =
    REPORT_PROCESSORS[reportType as keyof typeof REPORT_PROCESSORS];

  if (!processor) {
    return {
      property: [],
      person: [],
      phone: [],
      email: [],
      vehicle: [],
      obituary: [],
      username: [],
      contact: [],
      ancestor: [],
    };
  }

  let processedData: ProcessedReportData | null = null;

  // If the report type is obituary, the report data is already processed
  if (reportType === SEARCH_TYPES.OBITUARY) {
    processedData = reportData as ProcessedObituaryReportData;
  } else {
    processedData = processor(reportData, permalink || '');
  }

  if (!processedData) {
    return {
      property: [],
      person: [],
      phone: [],
      email: [],
      vehicle: [],
      obituary: [],
      username: [],
      contact: [],
      ancestor: [],
    };
  }

  const transformProcessedDataToSummaryFormat = (
    processed: ProcessedReportData,
    reportType: string
  ) => {
    const extractEmailFromPermalink = (permalink?: string): string => {
      if (!permalink) {
        return '';
      }
      const segments = permalink.split('/');
      const lastSegment = segments.pop() || '';
      // Validate it looks like an email
      return lastSegment.includes('@') ? lastSegment : '';
    };

    switch (reportType) {
      case SEARCH_TYPES.PROPERTY: {
        const propertyData = processed as ProcessedPropertyReportData;
        const rawOwners = reportData?.entities?.properties?.[0]?.owners || [];
        return {
          address: propertyData.data?.parcel_address?.full,
          property_type: propertyData.data?.propertyType,
          year_built: propertyData.data?.yearBuilt,
          bedrooms: propertyData.data?.bedrooms,
          bathrooms: propertyData.data?.bathrooms,
          square_footage: propertyData.data?.squareFootage,
          estimated_value: propertyData.data?.estimatedValue,
          taxes: propertyData.data?.propertyTax,
          tax_year: propertyData.data?.propertyTaxYear,
          lot_size: propertyData.data?.dimensions,
          owners: rawOwners,
        };
      }
      case SEARCH_TYPES.EMAIL: {
        const emailData = processed as ProcessedEmailReportData;
        const supportingEmail = (reportData as any)?.supporting?.emails?.[0];
        const emailAddress =
          supportingEmail?.address ||
          extractEmailFromPermalink(permalink) ||
          '';
        return {
          email: emailAddress,
          email_type: emailData?.data?.emails?.[0]?.type,
          fraud_score: supportingEmail?.fraud_score,
          owner_name: emailData.data?.ownerName,
          owner_phone: emailData.data?.phones?.[0],
          owner_address: emailData.data?.addresses?.[0],
        };
      }
      case SEARCH_TYPES.PHONE: {
        const phoneData = processed as ProcessedPhoneReportData;
        return {
          phone: phoneData.data?.phoneNumber,
          phone_type: phoneData.data?.lineType,
          provider: phoneData.data?.serviceProvider,
          active_status: phoneData.data?.isActive,
          risk: phoneData.data?.isRisky,
          is_spammer: phoneData.data?.isSpammer,
          is_do_not_call: phoneData.data?.isDoNotCall,
          associated_names: phoneData.data?.associatedNames,
          associated_emails: phoneData.data?.associatedEmails,
          owner_name: phoneData.data?.owner_fn,
        };
      }
      case SEARCH_TYPES.VEHICLE: {
        const flatVehicleReport = flatReportDataBySearchType(
          reportData,
          reportType
        ) as FlatVehicleReport | undefined;
        if (!flatVehicleReport) {
          return {
            flatVehicleReport: null,
          };
        }
        return {
          flatVehicleReport,
        };
      }
      case SEARCH_TYPES.PERSON: {
        const personData = processed as ProcessedPersonReportData;
        return {
          personName: personData.data?.personName,
          age: personData.data?.age,
          dateOfBirth: personData.data?.dateOfBirth,
          location: personData.data?.location,
          aliases: personData.data?.aliases,
          jobs: personData.data?.jobs,
          education: personData.data?.education,
          phone: personData.data?.phone,
          email: personData.data?.email,
          address: personData.data?.address,
          relatives: personData.data?.relatives,
        };
      }
      case SEARCH_TYPES.OBITUARY: {
        const obituaryData = processed as ProcessedObituaryReportData;
        return {
          personName: obituaryData.data?.personName,
          age: obituaryData.data?.age,
          dateOfBirth: obituaryData.data?.dateOfBirth,
          dateOfDeath: obituaryData.data?.dateOfDeath,
          placeOfBirth: obituaryData.data?.placeOfBirth,
          location: obituaryData.data?.location,
          relatives: obituaryData.data?.relatives,
          obituaryText: obituaryData.data?.obituaryText,
        };
      }
      case SEARCH_TYPES.CONTACT: {
        const contactData = processed as ProcessedContactReportData;
        return {
          personName: contactData.data?.personName,
          age: contactData.data?.age,
          dateOfBirth: contactData.data?.dateOfBirth,
          location: contactData.data?.location,
          aliases: contactData.data?.aliases,
          phone: contactData.data?.phone,
          email: contactData.data?.email,
          address: contactData.data?.address,
          relatives: contactData.data?.relatives,
          jobs: contactData.data?.jobs,
          education: contactData.data?.education,
          social: contactData.data?.social,
        };
      }
      case SEARCH_TYPES.ANCESTOR: {
        const ancestorData = processed as ProcessedAncestorReportData;
        return {
          personName: ancestorData.data?.personName,
          aliases: ancestorData.data?.aliases,
          dateOfBirth: ancestorData.data?.dateOfBirth,
          dateOfDeath: ancestorData.data?.dateOfDeath,
          birthAddress: ancestorData.data?.birthAddress,
          deathAddress: ancestorData.data?.deathAddress,
          burialAddress: ancestorData.data?.burialAddress,
          burialDate: ancestorData.data?.burialDate,
          familySearchId: ancestorData.data?.familySearchId,
          location: ancestorData.data?.location,
          relatives: ancestorData.data?.relatives,
          facts: ancestorData.data?.facts,
          age: ancestorData.data?.age,
        };
      }
      case SEARCH_TYPES.USERNAME: {
        const usernameData = processed as ProcessedUsernameReportData;
        return {
          username_presence: usernameData.data?.username_presence,
          potential_owner: usernameData.data?.potential_owner,
        };
      }
      default:
        return {};
    }
  };

  const transformedData = transformProcessedDataToSummaryFormat(
    processedData,
    reportType
  );

  const summaryData: {
    username?: Array<Record<string, any>>;
    property?: PropertySummaryData[];
    email?: EmailSummaryData[];
    vehicle?: VehicleReportSummaryData[];
    phone?: Array<Record<string, any>>;
    person?: Array<Record<string, any>>;
    obituary?: Array<Record<string, any>>;
    contact?: Array<Record<string, any>>;
    ancestor?: Array<Record<string, any>>;
    // TODO: Add other report types here
  } = {
    username: [],
    property: [],
    email: [],
    phone: [],
    vehicle: [],
    person: [],
    obituary: [],
    contact: [],
    ancestor: [],
    // TODO: Add other report types here
  };

  switch (reportType) {
    case SEARCH_TYPES.PROPERTY:
      summaryData.property = [transformedData as PropertySummaryData];
      break;
    case SEARCH_TYPES.EMAIL:
      summaryData.email = [transformedData as EmailSummaryData];
      break;
    case SEARCH_TYPES.VEHICLE:
      summaryData.vehicle = [transformedData as VehicleReportSummaryData];
      break;
    case SEARCH_TYPES.PHONE:
      summaryData.phone = [transformedData];
      break;
    case SEARCH_TYPES.PERSON:
      summaryData.person = [transformedData];
      break;
    case SEARCH_TYPES.OBITUARY:
      summaryData.obituary = [transformedData];
      break;
    case SEARCH_TYPES.CONTACT:
      summaryData.contact = [transformedData];
      break;
    case SEARCH_TYPES.ANCESTOR:
      summaryData.ancestor = [transformedData];
      break;
    case SEARCH_TYPES.USERNAME:
      summaryData.username = [transformedData];
      break;
    // TODO: Add other report types here
    default:
      break;
  }

  return summaryData;
};

export const processAncestorSearchReportForOverviewAndOwners = (
  data: GenericReportResponse | null,
  permalink: string
) => {
  if (!data) {
    return null;
  }

  let report: PersonReport | null;
  const coreResult = data as any;

  if (coreResult?.entities) {
    report = ReportFactory.create(coreResult as any, 'person');
  } else {
    report = ReportFactory.create(coreResult?.data as any, 'person');
  }

  if (!report) {
    return null;
  }

  const person = report?.data?.people?.[0];

  if (!person) {
    return null;
  }

  const fullName = person?.identity?.names?.[0]?.full || '';
  const otherNames =
    person?.identity?.names?.slice(1)?.map((name: any) => nameize(name.full)) ||
    [];
  const dateOfBirthObj = person?.identity?.dobs?.[0]?.date;
  const dateOfDeathObj = person?.identity?.dods?.[0]?.date;
  const familySearchId = person?.identity?.fs_ids?.[0] || '';

  const birthAddress =
    person?.facts?.find((fact: any) => fact?.type?.toLowerCase() === 'birth')
      ?.place?.full || '';

  const deathAddress =
    person?.facts?.find((fact: any) => fact?.type?.toLowerCase() === 'death')
      ?.place?.full || '';

  const burialData = person?.facts?.find(
    (fact: any) => fact?.type?.toLowerCase() === 'burial'
  );
  const burialAddress = burialData?.place?.full || '';

  const addressInfo = person?.contact?.addresses?.[0];
  const location = addressInfo?.full || '';

  const personRelatives =
    person?.rawData?.connections?.relatives
      ?.filter((relative: any) => {
        const relativeName = relative?.identity?.names?.[0]?.full || '';
        return (
          relativeName &&
          relative?.identity?.fs_ids?.length &&
          relative?.identity?.fs_ids[0] !== 'UNKNOWN'
        );
      })
      ?.map((relative: any) => relative?.identity?.names?.[0]?.full || '') ||
    [];

  const FILTER_FACTS_TYPES = ['Death', 'Birth', 'Burial'];
  const facts = Array.isArray(person?.facts)
    ? person.facts
        .filter((fact: any) => !FILTER_FACTS_TYPES.includes(fact?.type || ''))
        .map((fact: any) => ({
          type: fact?.type || '',
          date: fact?.date?.full || '',
          place: fact?.place?.full || '',
        }))
    : [];

  const age = person?.identity?.ages?.[0]?.years || '';

  return {
    permalink,
    data: {
      personName: nameize(fullName),
      aliases: otherNames,
      dateOfBirth: dateOfBirthObj?.full || '',
      dateOfDeath: dateOfDeathObj?.full || '',
      birthAddress,
      deathAddress,
      burialAddress,
      burialDate: burialData?.date?.full || '',
      familySearchId,
      location,
      relatives: personRelatives,
      facts,
      age,
      reportType: 'ancestor',
    },
  };
};

export const flatReportDataBySearchType = (
  reportData: GenericReportResponse,
  searchType: string
):
  | FlatPhoneReport
  | FlatPersonReport
  | FlatPropertyReport
  | FlatVehicleReport
  | FlatUsernameReport
  | undefined => {
  switch (searchType) {
    case 'phone':
      return mapPhoneReportToFlatSections(reportData);
    case 'person':
      return mapPersonReportToFlatSections(reportData);
    case 'property':
      return mapPropertyReportToFlatSections(reportData);
    case 'email':
      return mapEmailReportToFlatSections(reportData);
    case 'vehicle':
      return mapVehicleReportToFlatSections(reportData);
    case 'username':
      return mapUsernameReportToFlatSections(reportData);
    default:
      return undefined;
  }
};

export const mapReportTypeFromRecentReportsToChatType = (
  reportType: string
): string | null => {
  const typeMap: Record<string, string> = {
    detailed_person_report: SEARCH_TYPES.PERSON,
    reverse_phone_report: SEARCH_TYPES.PHONE,
    property_report: SEARCH_TYPES.PROPERTY,
    vehicle_report: SEARCH_TYPES.VEHICLE,
    social_network_report: SEARCH_TYPES.EMAIL,
    obituary_report: SEARCH_TYPES.OBITUARY,
    ancestry_report: SEARCH_TYPES.ANCESTOR,
    contact_report: SEARCH_TYPES.CONTACT,
    username_report: SEARCH_TYPES.USERNAME,
  };

  const mappedType = typeMap[reportType];

  if (Object.values(SEARCH_TYPES).includes(reportType as any)) {
    return reportType;
  }

  return mappedType || null;
};
